import * as Sentry from '@sentry/browser';
import { history, RunTimeLayoutConfig } from '@umijs/max';

import RightContent from '@/components/RightContent';
import MenuRender from '@/components/SiderMenu/MenuRender';
import ErrorBoundary from '@/components/ErrorBoundary';
import MetaSurchargeGlobalNotice from '@/components/MetaSurchargeModal/GlobalNotice';
import { getUserInfo, getBusinessInfo } from '@/services/user';
import { eventFeedback } from '@/services/user';
import { getCGrayInfo, getGrayInfo } from '@/services/grayInfo';
import { requestConfig } from '@/utils/request';
import { fetchSuccess, gtagEventBuriedPoint, initQiniuHost } from '@/utils/utils';
import { setUsername, setuserRole } from '@/tracks';
import { CGrayInfo, GrayInfo } from "@/interface/gray";
import { transformPageTitle } from '@/utils/i18n';

import i18n from './i18n';
import defaultSettings from '../config/defaultSettings';
import { type SettlementType } from './utils/enum';
import { version } from '../package.json';
import { checkReloadApp } from './utils/versionCheck';
import { opsigenesInit } from '@/opsigenesSDK';
import { parse } from 'qs';
import { isSkippedTour } from './utils/config';
import { homePages, META_UTM_STORAGE_KEY, ADVERTISER_IDENTIFIER_KEY } from './utils/constant';
import Logo from './components/SiderMenu/Logo';
import styles from './app.less';

import '@bluevision/components/index.css';
import { identifyUser, initPostHog } from './posthog';

// 若需要修改根容器，可结合 rootContainer（可选）
export function rootContainer(container: React.ReactElement) {
  initPostHog(); // 也可在此初始化（确保只执行一次）
  return container;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__APP_VERSION = version;

const ignoreLogin = ['/user', '/protocol', '/partner', '/guide'];

const fetchUserInfo = async () => {
  try {
    const res = await getUserInfo();

    if (fetchSuccess(res)) {
      // 设置 user 到 sentry 上，跟随每一次错误上报
      Sentry.setUser(res.data);

      identifyUser(res.data?.username, {
        name: res.data?.nickname,
        email: res.data?.username,
      });

      return res.data;
    }
  } catch (error) {}

  return undefined;
};

const fetchBusinessInfo = async () => {
  try {
    const res = await getBusinessInfo();

    if (fetchSuccess(res)) {
      return res.data;
    }
  } catch (e) {}

  return undefined;
};

const fetchGrayInfo = async () => {
  try {
    const res = await getGrayInfo();

    if (fetchSuccess(res)) {
      return res.data;
    }
  } catch (e) {}

  return undefined;
};

const fetchCGrayInfo = async () => {
  try {
    const res = await getCGrayInfo();

    if (fetchSuccess(res)) {
      return res.data;
    }
  } catch (e) {}

  return undefined;
};

export async function getInitialState(): Promise<{
  currentUser?: API.UserInfo;
  businessInfo?: Partial<API.BusinessInfo<SettlementType>>;
  grayInfo?: GrayInfo;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.UserInfo | undefined>;
  fetchBusinessInfo?: () => Promise<
    API.BusinessInfo<SettlementType> | undefined
  >;
  fetchGrayInfo?: () => Promise<GrayInfo | undefined>;
  fetchCGrayInfo?: () => Promise<CGrayInfo | undefined>;
  cGrayInfo?: CGrayInfo
}> {
  // 代理商 code — 使用 window.location.search 确保首次加载能拿到浏览器真实 URL 参数
  const searchStr = window.location.search;
  const query = parse(searchStr, {
    ignoreQueryPrefix: true,
    comma: true,
  })
  // 生成 advertiser_identifier（所有访问场景均生成，用于联系销售接口上报）
  let advertiserId = localStorage.getItem(ADVERTISER_IDENTIFIER_KEY);
  if (!advertiserId) {
    advertiserId = crypto.randomUUID();
    localStorage.setItem(ADVERTISER_IDENTIFIER_KEY, advertiserId);
  }

  const code = query?.code || '';
  if (code) {
    localStorage.setItem('agent_code', code as string);
  }

  // 检测到 meta_utm 参数时，存储整段 url 参数并触发 Meta 事件回传
  if (query?.meta_utm) {
    localStorage.setItem(META_UTM_STORAGE_KEY, searchStr);
    // 防重复：同一 searchStr 在本次会话中只触发一次事件回传
    const PV_SENT_KEY = 'meta_pv_sent';
    const lastSent = sessionStorage.getItem(PV_SENT_KEY);
    if (lastSent !== searchStr) {
      sessionStorage.setItem(PV_SENT_KEY, searchStr);
      gtagEventBuriedPoint('Meta官方引流', {
        meta_utm: query.meta_utm as string,
      });
      const trackingParams = parse(searchStr, { ignoreQueryPrefix: true });
      eventFeedback({ advertiser_identifier: advertiserId, event_type: 'page_visit', ...trackingParams }).catch(() => {});
    }
  } else {
    localStorage.removeItem(META_UTM_STORAGE_KEY);
  }

  // 如果不是登录页面，执行
  if (!ignoreLogin.some((item) => location.pathname.startsWith(item))) {
    const currentUser = await fetchUserInfo();
    setUsername(currentUser?.username);
    setuserRole(currentUser?.is_business_member);
    let businessInfo: Partial<API.BusinessInfo<SettlementType>> | undefined =
      undefined;

    if (currentUser) {
      /** 获取文件访问域名（接口需要登录），不阻塞页面初始化 */
      initQiniuHost();
      businessInfo = await fetchBusinessInfo();
    }

    const grayInfo = await fetchGrayInfo();
    const cGrayInfo = await fetchCGrayInfo();

    if (!isSkippedTour() && !homePages.includes(location.pathname)) {
      history.push('/opening-account/outline');
    }

    return {
      currentUser,
      fetchUserInfo,
      businessInfo,
      fetchBusinessInfo,
      grayInfo,
      fetchGrayInfo,
      fetchCGrayInfo,
      cGrayInfo,
    };
  }

  return {
    fetchUserInfo,
    fetchBusinessInfo,
  };
}

export const layout: RunTimeLayoutConfig = () => {
  return {
    siderWidth: 240,
    rightContentRender: () => <RightContent />,
    disableContentMargin: false,
    // 路由切换时触发
    onPageChange: () => {
      checkReloadApp();
    },
    menuRender: (props, dom) => (
      <MenuRender
        config={props}
        dom={dom as React.ReactElement}
        collapsedWidth={72}
      />
    ),
    pageTitleRender: (_, __, info) => {
      return transformPageTitle(info?.title ?? i18n.t('luban_plus'));
    },
    // 进入后台后统一挂载登录后的 Meta 广告附加费通知
    childrenRender: (children) => (
      <>
        {children}
        <MetaSurchargeGlobalNotice />
      </>
    ),
    ErrorBoundary,
    ...defaultSettings,
    // 根据用户角色判断显示不同的 logo
    logo: <Logo className={styles.logo} />,
    };
};

/** 全局非 React 错误捕获 */
window.addEventListener('error', (event: unknown) => {
  Sentry?.captureException(event, {
    extra: { type: 'global error catch' },
  });
});
// 初始化linkai客服
opsigenesInit();

export const request = requestConfig;
